import useTranslation from 'next-translate/useTranslation'

import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'

import { TrialInput, TrialSelect } from '.'
import { useTrial } from '../TrialProvider'

type Props = {
	formId: string
	ctaText: string
	handleOnSubmit: SubmitHandler<FieldValues>
}

export function TrialFields({ formId, ctaText, handleOnSubmit }: Props) {
	const { t } = useTranslation('trials')

	const {
		currentStep,
		formFields,
		disableButton,
		recaptchaRef,
		recaptchaError,
		setShowWebsiteField
	} = useTrial()

	const { handleSubmit, control } = useForm({ mode: 'onTouched' })

	const getErrorMessage = (fieldName: string, errorType: string) => {
		return t(`trialForm.fields.${fieldName}.errors.${errorType}`)
	}

	return (
		<form
			onSubmit={handleSubmit(handleOnSubmit)}
			className="trial-form pt-10"
			id={formId}
			aria-label="trial-form"
		>
			<div className="space-y-8 md:space-y-12">
				{formFields.map((field) => {
					if (field.fieldType === 'select') {
						return (
							<TrialSelect
								key={field.fieldName}
								className="px-0 text-bc-black pr-10 appearance-none bg-blend-color-dodge border-none focus:border-none focus:ring-0"
								name={field.fieldName}
								label={t(`trialForm.fields.${field.fieldName}.label`)}
								control={control}
								defaultValue={field.defaultValue ?? ''}
								rules={{ required: getErrorMessage(field.fieldName, 'required') }}
								hintText={field.hintText ? t(`trialForm.fields.${field.fieldName}.hintText`) : null}
								setShowWebsiteField={setShowWebsiteField}
							>
								{field.options?.map((option) => (
									<option key={option.id} value={option.value}>
										{t(`trialForm.fields.${field.fieldName}.options.${option.value}`)}
									</option>
								))}
							</TrialSelect>
						)
					} else {
						return (
							<TrialInput
								key={field.fieldName}
								className="px-0 border-none focus:border-none focus:ring-0"
								type={field.fieldType}
								name={field.fieldName}
								label={t(`trialForm.fields.${field.fieldName}.label`)}
								control={control}
								rules={{
									required: field.required ? getErrorMessage(field.fieldName, 'required') : false,
									minLength: field.minLength && {
										value: field.minLength,
										message: getErrorMessage(field.fieldName, 'minLength')
									},
									maxLength: field.maxLength && {
										value: field.maxLength,
										message: getErrorMessage(field.fieldName, 'maxLength')
									},
									pattern: field.pattern && {
										value: field.pattern,
										message: !(field.fieldName === 'phone_number' || field.fieldName === 'password')
											? getErrorMessage(field.fieldName, 'invalid')
											: null
									}
								}}
								hintText={field.hintText ? t(`trialForm.fields.${field.fieldName}.hintText`) : null}
							/>
						)
					}
				})}
			</div>

			{currentStep === 1
				? recaptchaRef && <div className="pt-8 lg:pt-12" ref={recaptchaRef} />
				: null}

			{recaptchaError && (
				<p className="text-xs text-bc-pink">{t('trialForm.fields.recaptcha.errors.required')}</p>
			)}

			<div className="pt-6">
				<button
					type="submit"
					className="text-white text-sm font-medium px-6 py-3 bg-bc-blue rounded-md hover:bg-blue-200 transition-all uppercase"
					disabled={disableButton}
				>
					{ctaText}
				</button>
			</div>
		</form>
	)
}
